import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouteComponentProps, useParams } from 'react-router';
import parse from 'html-react-parser';
import _ from 'lodash';

import { Row, Col, Tabs, Button, Image } from 'antd';

import { RootState } from '../../../application/store';
import { BUSINESS_UNIT_TYPES } from '../../../utils/constants/business-unit-types';
import { initWideFormOrderLine, initWideFormProduct, toggleWideFormDrawer } from '../../../slices/WideForm/wideFormSlice';
import { useProductsDetail } from './hooks/useProductsDetail';
import { useProduct } from '../../../common/hooks/useProduct';

import './products-detail-page.scss';

import BVProductsTemplate from '../../../common/templates/BVProductsTemplate/bv-products-template';
import BVEmpty from '../../../common/components/BVEmpty/bv-empty';
import BVShimmer from '../../../common/components/BVShimmers/BVShimmer/bv-shimmer';
import BVProductPrice from '../../../common/components/BVProductPrice/bv-product-price';
import BVDestinations from './components/BVDestinations/bv-destinations';
import BVCompactForm from './components/BVCompactForm/bv-compact-form';
import WideFormDrawer from '../../../common/components/BVDrawers/WideFormDrawer/wide-form-drawer';

const {
    TabPane
} = Tabs;

interface IRouteParams {
    businessUnitCode: string,
    productId: string
}

const ProductsDetailPage: React.FunctionComponent<RouteComponentProps> = (props) => {
    const {
        handleProductsDetail,
        productsDetail,
        productsDetailLoading,
        handleProductSizes,
        productSizes,
        productSizesLoading
    } = useProduct();

    const {
        handleProductsAttributes,
        productsAttributes,
        productsAttributesLoading
    } = useProductsDetail();

    const {
        businessUnitCode,
        productId
    } = useParams<IRouteParams>();

    const bvCustomer = useSelector((state: RootState) => state.customer);

    useEffect(() => {
        if (productId && bvCustomer.id) {
            handleProductsDetail(parseInt(productId));
            handleProductsAttributes(parseInt(productId));
            handleProductSizes(parseInt(productId), bvCustomer.id);
        }
    }, [productId]);

    const dispatch = useDispatch();

    const bvWideForm = useSelector((state: RootState) => state.wideForm);

    return (
        <BVProductsTemplate {...props}>
            <div className="products-detail-container">
                <div className="products-detail-banner">
                    <Row gutter={[48, 0]}>
                        <Col span={12} style={{ textAlign: 'center', alignSelf: 'center' }}>
                            <BVShimmer loading={productsDetailLoading} type={'shimmer-image'} style={{ width: '100%' }}>
                                {_.includes(productsDetail?.image?.detailUrl, 'bv-product-placeholder')
                                    ?
                                    <div className="ant-image">
                                        <img
                                            className="ant-image-img"
                                            src={productsDetail?.image?.detailUrl}
                                            alt={productsDetail?.descrShort}
                                        />
                                    </div>
                                    :
                                    <Image
                                        src={productsDetail?.image?.detailUrl}
                                        alt={productsDetail?.descrShort}
                                        preview={{
                                            className: "products-detail-mask"
                                        }}
                                    />
                                }
                            </BVShimmer>
                        </Col>
                        <Col span={12}>
                            <div className="products-detail-body">
                                <BVShimmer loading={productsDetailLoading} type={'shimmer-details'} style={{ width: '100%' }}>
                                    <div className="detail-wrapper">
                                        <span className="heading">
                                            {productsDetail?.descrShort || '-'}
                                        </span>

                                        <span className="secondary-heading">
                                            <BVShimmer loading={productSizesLoading} type={'shimmer-details'}>
                                                {businessUnitCode === BUSINESS_UNIT_TYPES.JEWELRY_CODE &&
                                                    <span>
                                                        {`CatCode: ${productsDetail?.catalogCode}`}
                                                    </span>
                                                }
                                                {productSizes && productSizes.length === 1 &&
                                                    <span>
                                                        {`SapCode: ${productSizes[0].sapCode}`}
                                                    </span>
                                                }
                                            </BVShimmer>
                                        </span>

                                        <span className="text decription-short">
                                            {productsDetail?.descrShortMarketing || '-'}
                                        </span>
                                        <span className="text">
                                            {parse(productsDetail?.descrLongMarketing || '')}
                                        </span>
                                    </div>
                                </BVShimmer>

                                <BVShimmer loading={productSizesLoading} type={'shimmer-price'} style={{ width: '100%' }}>
                                    <BVProductPrice
                                        className="bv-product-details-price"
                                        sizes={productSizes}
                                    />

                                    {productSizes && productSizes.length === 1
                                        ?
                                        <BVCompactForm
                                            businessUnitCode={businessUnitCode}
                                            product={productsDetail}
                                            productSizes={productSizes}
                                            productSizesLoading={productSizesLoading}
                                            isEmbedded={false}
                                        />
                                        :
                                        <Button
                                            type="primary"
                                            disabled={productSizesLoading}
                                            onClick={() => {
                                                dispatch(initWideFormProduct(parseInt(productId)));
                                                dispatch(toggleWideFormDrawer(true));
                                            }}
                                        >
                                            {'Add to cart'}
                                        </Button>
                                    }
                                </BVShimmer>
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="products-detail-tabs">
                    <Tabs defaultActiveKey={'1'} centered>
                        <TabPane className="details-tab" tab="Ship To" key={'1'}>
                            <BVShimmer loading={productSizesLoading} type={'shimmer-card'}>
                                <BVDestinations
                                    disabled={productsDetailLoading}
                                    product={productsDetail}
                                />
                            </BVShimmer>
                        </TabPane>
                        <TabPane className="details-tab" tab="More" key={'2'}>
                            <Row gutter={[48, 0]} align="top">
                                <Col span={24}>
                                    <span className="heading">
                                        {'Details'}
                                    </span>
                                </Col>

                                <BVShimmer loading={productsAttributesLoading} type={'shimmer-details'}>
                                    <BVEmpty dataSource={productsAttributes || []} disclaimer={'No additional info found for current product.'}>
                                        {productsAttributes && productsAttributes.length > 0 &&
                                            _.chunk(productsAttributes, Math.round(productsAttributes.length / 4)).map((chunkItem, chunkIndex) => (
                                                <Col key={chunkIndex} span={6}>
                                                    {_.map(chunkItem, (dataItem, dataIndex) => (
                                                        <div key={dataIndex} className="additional-info">
                                                            <span className="secondary-heading">
                                                                {`${dataItem.name}:`}
                                                            </span>
                                                            <span className="text">
                                                                {dataItem.value}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </Col>
                                            ))
                                        }
                                    </BVEmpty>
                                </BVShimmer>
                            </Row>
                        </TabPane>
                    </Tabs>
                </div>
            </div>

            <WideFormDrawer
                productId={bvWideForm.productId}
                orderLineId={bvWideForm.orderLineId}
                drawerVisible={bvWideForm.wideFormDrawerVisible}
                onDispose={() => {
                    dispatch(initWideFormOrderLine(-1));
                    dispatch(initWideFormProduct(-1));
                    dispatch(toggleWideFormDrawer(false));
                }}
            />
        </BVProductsTemplate>
    );
}

export default ProductsDetailPage;